/**
 * Contacts Service
 * API service for managing contacts
 */

import { supabase } from "./supabase";
import { addContactTag, removeContactTag } from "./contactTags.service";
import { voucherStatusLabel } from "./redemptions.service";
import { isSmsNumberBusy, retryOnSmsNumberBusy } from "../utils/smsNumberBusy";
import { contactPickerCanSearch } from "../utils/contactPickerStatus";
import type {
  Contact,
  ContactFilters,
  ContactRelationship,
  ClassCompletion,
} from "../types/contact.types";
import type { UUID } from "../types/common.types";
import type { PaginatedResponse, PaginationParams } from "../types/api.types";

export interface ContactSubscription {
  id: string;
  contact_id: string;
  zuora_subscription_id?: string;
  subscription_number?: string;
  status?: string;
  term_start_date?: string;
  term_end_date?: string;
  subscription_start_date?: string;
  auto_renew?: boolean;
  payment_frequency?: string;
  subscription_type?: string;
  subscription_source?: string;
  region?: string;
  policy_id?: string;
  product_names?: string[];
  rate_plan_details?: Record<string, unknown>;
  cancellation_reason?: string;
  created_at: string;
  updated_at: string;
}

export interface HouseholdMember {
  id: string;
  first_name: string;
  last_name: string;
  is_primary_member: boolean | null;
  member_status: string | null;
}

/**
 * Get contact by ID
 */
export const getContact = async (id: UUID): Promise<Contact> => {
  const { data, error } = await supabase
    .from("contacts")
    .select("*")
    .eq("id", id)
    .single();

  if (error) {
    throw error;
  }
  return data;
};

/**
 * Extra fields a SEARCHED contacts page carries (search_contacts_list, migration 770). Absent on the
 * unsearched list, whose behaviour is unchanged.
 * - totalCapped: more than `total` (1,000) contacts match; the page shows "1,000+".
 * - incomplete: a type / e-mail-status filter hid most of the rows the search could read, so there may be
 *   more matches than `total`; the page asks for a narrower search.
 * - searchRefused: the term was too short / too broad to search (nothing was looked up).
 */
export interface ContactsSearchMeta {
  totalCapped?: boolean;
  incomplete?: boolean;
  searchRefused?: "too_short" | "too_broad" | null;
}

/**
 * Get contacts with pagination and filtering
 */
export const getContacts = async (
  params: PaginationParams,
  filters?: ContactFilters,
): Promise<PaginatedResponse<Contact> & ContactsSearchMeta> => {
  const searchTerm = filters?.search?.trim();

  if (searchTerm) {
    // Server-side search (migration 2026-10-15-770, search_contacts_list): the picker's matching rules
    // (First Last in either order, phone in any format, partial e-mail, member #), the page's filters,
    // and a total capped at 1,000 so a common name never counts or sorts every match. Staff-only
    // (42501 otherwise) -- the /contacts route admits exactly the six internal-staff roles.
    const { data, error } = await supabase.rpc("search_contacts_list", {
      p_query: searchTerm,
      p_limit: params.limit,
      p_offset: (params.page - 1) * params.limit,
      p_organization_id: filters?.organization_id ?? null,
      p_lifecycle_stage: filters?.lifecycle_stage ?? null,
      p_assigned_agent_id: filters?.assigned_agent_id ?? null,
      p_email_suppression_status: filters?.email_suppression_status ?? null,
    });

    if (error) {
      (
        error as Error & { __diagnostic?: Record<string, unknown> }
      ).__diagnostic = {
        where: "getContacts.search_contacts_list",
        pagination: { page: params.page, limit: params.limit },
        filters_summary: {
          has_search: true,
          search_term_length: searchTerm.length,
          search_first_8: searchTerm.slice(0, 8),
          search_last_4: searchTerm.slice(-4),
          search_contains_at: searchTerm.includes("@"),
          organization_id: filters?.organization_id ?? null,
          lifecycle_stage: filters?.lifecycle_stage ?? null,
          assigned_agent_id: filters?.assigned_agent_id ?? null,
          email_suppression_status: filters?.email_suppression_status ?? null,
        },
        postgrest: {
          code: (error as { code?: string }).code ?? null,
          details: (error as { details?: string }).details ?? null,
          hint: (error as { hint?: string }).hint ?? null,
          message: error.message,
        },
      };
      throw error;
    }

    const rpcResult = (data ?? {}) as {
      data?: Contact[];
      total?: number;
      total_capped?: boolean;
      incomplete?: boolean;
      refused?: "too_short" | "too_broad" | null;
    };

    return {
      data: rpcResult.data || [],
      total: rpcResult.total || 0,
      page: params.page,
      limit: params.limit,
      totalCapped: rpcResult.total_capped === true,
      incomplete: rpcResult.incomplete === true,
      searchRefused: rpcResult.refused ?? null,
    } as PaginatedResponse<Contact> & ContactsSearchMeta;
  }

  // Use estimated count for unfiltered queries and text search to avoid timeout on 600K+ rows.
  // ILIKE with leading wildcard can't use btree indexes, so search queries are expensive.
  // Exact count only when indexed filters (organization_id, lifecycle_stage, etc.) narrow the set.
  const hasIndexedFilters = !!(
    filters?.organization_id ||
    filters?.lifecycle_stage ||
    filters?.assigned_agent_id ||
    filters?.email_suppression_status
  );
  let query = supabase
    .from("contacts")
    .select("*", { count: hasIndexedFilters ? "exact" : "estimated" });

  // Track the search shape so the final ORDER BY can adapt. Name
  // searches need ORDER BY last_name,first_name to escape the
  // ORDER-BY-created_at + LIMIT planner trap that walks the whole
  // 1.4M-row table on PROD for highly-selective filters. Email and
  // member-id searches are point-lookups and don't need the override.
  let searchMode: "none" | "email" | "member_id" | "name" = "none";

  if (filters?.search) {
    // Smart-detection search (2026-05-23 audit AI v1 scope):
    //   1. "@" in term → exact email lookup (2026-05-19 RLS×wildcard fix)
    //   2. Looks like a member# (digits / letters+digits, no spaces) →
    //      prefix match on membership_id (idx_contacts_membership_id
    //      btree handles this efficiently). 99.7% of prod member#s are
    //      [A-Z0-9-]+ — most commonly state-prefixed like 'TX975543'.
    //   3. Otherwise → name search. Multi-word "Firstname Lastname"
    //      becomes AND-of-first-token + last-token prefixes (high
    //      selectivity, planner-friendly). Single word stays
    //      OR-of-prefix across both name columns.
    //
    // OUT OF SCOPE for v1 (deferred to follow-up):
    //   - Phone search: needs phone_normalized column to handle format
    //     variance (e.g. operator types "555-1234" but contact is
    //     stored as "+15551234"). Without normalization, false misses.
    //   - Address (address_line1) search: no index; trigram would be
    //     needed before this is safe at 631k rows under RLS.
    //   - searchContacts picker (services/contacts.service.ts) keeps
    //     its 8-field %term% OR breadth for picker UX. Different
    //     trade-off; reassess separately if it shows similar timeout
    //     pressure on PROD.
    //   - Reverse "Lastname Firstname" 2-word ordering. Operators are
    //     expected to type firstname-first. Document in UX if needed.
    const term = filters.search.trim();
    if (term.includes("@")) {
      searchMode = "email";
      query = query.eq("email", term.toLowerCase());
    } else {
      const sanitized = term.replace(/[,()."*\\]/g, "");
      if (sanitized) {
        // Membership-id shape: starts with letter or digit, contains
        // only [A-Z0-9-] (case-insensitive), at least 2 chars total.
        // Covers TX975543, 674810, MEM-CLEAR-001 patterns. Excludes
        // spaces (so "john smith" goes to name search).
        const looksLikeMemberId =
          /^[A-Za-z0-9][A-Za-z0-9-]+$/.test(sanitized) &&
          /[0-9]/.test(sanitized);
        if (looksLikeMemberId) {
          searchMode = "member_id";
          query = query.ilike("membership_id", `${sanitized.toUpperCase()}%`);
        } else {
          // Name search. Tokenize on whitespace. Multi-word becomes
          // AND of first-token + last-token prefixes ("Firstname
          // Lastname" intent — overwhelmingly common). Single-word
          // stays OR-of-prefix. The AND form is much more selective
          // and avoids the OR-recheck cost we saw on PROD (1.4M rows
          // scanned for "Mikayla ward%" → 10s timeout).
          searchMode = "name";
          const tokens = sanitized.split(/\s+/).filter((t) => t.length > 0);
          if (tokens.length >= 2) {
            query = query
              .ilike("first_name", `${tokens[0]}%`)
              .ilike("last_name", `${tokens[tokens.length - 1]}%`);
          } else {
            // PostgREST .or() STRING filters use '*' as the LIKE wildcard, not
            // '%' (which isn't sent as a wildcard here and silently matched
            // nothing — single-word name searches returned 0 despite matches).
            // The multi-word branch above is unaffected: it uses the dedicated
            // .ilike() method, where '%' is correct.
            query = query.or(
              `first_name.ilike.${sanitized}*,last_name.ilike.${sanitized}*`,
            );
          }
        }
      }
    }
  }

  if (filters?.organization_id) {
    query = query.eq("organization_id", filters.organization_id);
  }

  // Filter by lifecycle stage (lead, prospect, customer, partner, inactive)
  if (filters?.lifecycle_stage) {
    query = query.eq("lifecycle_stage", filters.lifecycle_stage);
  }

  // Filter by assigned support agent.
  // NOTE the column: contacts has BOTH `assigned_agent_id` and `assigned_support_agent_id`.
  // Only `assigned_support_agent_id` is written (1,577,287 rows on PROD); `assigned_agent_id`
  // is vestigial with ZERO non-null rows, so filtering on it returned an empty list for every
  // agent. The filter key stays `assigned_agent_id` because that is the public ContactFilters
  // shape and the search_contacts_page RPC parameter name; only the COLUMN changes.
  if (filters?.assigned_agent_id) {
    query = query.eq("assigned_support_agent_id", filters.assigned_agent_id);
  }

  // Email suppression filter — backed by idx_contacts_is_email_suppressed
  // (partial index WHERE is_email_suppressed = true).
  if (filters?.email_suppression_status) {
    const s = filters.email_suppression_status;
    if (s === "active") {
      query = query.eq("is_email_suppressed", false);
    } else if (s === "any_suppressed") {
      query = query.eq("is_email_suppressed", true);
    } else {
      query = query
        .eq("is_email_suppressed", true)
        .eq("email_suppression_reason", s);
    }
  }

  // For name searches, force ORDER BY last_name, first_name. The
  // default ORDER BY created_at DESC combined with a selective ILIKE
  // filter steers the planner into idx_contacts_created scan-and-
  // filter, which walks the entire 1.4M-row PROD table when the
  // filter is highly selective. Name sort triggers a Seq Scan +
  // top-N heapsort path, bounded at ~80k buffers (~1s).
  // Email + member-id searches are point-lookups via dedicated
  // indexes, so the override doesn't apply.
  const ordered =
    searchMode === "name"
      ? query
          .order("last_name", { ascending: true })
          .order("first_name", { ascending: true })
      : query.order(params.sortBy || "created_at", {
          ascending: params.sortOrder === "asc",
        });

  const { data, error, count } = await ordered.range(
    (params.page - 1) * params.limit,
    params.page * params.limit - 1,
  );

  if (error) {
    // 2026-06-02 instrumentation: attach the inputs that produced this
    // error so the caller can capture a meaningful diagnostic without
    // requiring DevTools network-tab inspection. Operators have
    // reported intermittent "Error loading contacts" on PROD without
    // being able to extract the failing PostgREST response.
    (error as Error & { __diagnostic?: Record<string, unknown> }).__diagnostic =
      {
        where: "getContacts",
        pagination: { page: params.page, limit: params.limit },
        filters_summary: {
          has_search: !!filters?.search,
          search_term_length: filters?.search?.length ?? 0,
          search_first_8: filters?.search?.slice(0, 8) ?? null,
          search_last_4: filters?.search?.slice(-4) ?? null,
          search_contains_at: filters?.search?.includes("@") ?? false,
          organization_id: filters?.organization_id ?? null,
          lifecycle_stage: filters?.lifecycle_stage ?? null,
          assigned_agent_id: filters?.assigned_agent_id ?? null,
          email_suppression_status: filters?.email_suppression_status ?? null,
        },
        postgrest: {
          code: (error as { code?: string }).code ?? null,
          details: (error as { details?: string }).details ?? null,
          hint: (error as { hint?: string }).hint ?? null,
          message: error.message,
        },
      };
    throw error;
  }

  return {
    data: data || [],
    total: count || 0,
    page: params.page,
    limit: params.limit,
  };
};

/**
 * Audit R4-T6 (2026-09-30): a contact write that changes or clears a phone number can be refused by the SMS
 * number-suppression trigger with SMS_NUMBER_BUSY (SQLSTATE 55P03) when another transaction holds that number's
 * gate for a moment. The trigger aborts the whole statement, so NOTHING was written and re-running the same
 * statement is safe. Retry ONLY that refusal, a bounded number of times; every other error (including network
 * errors, where a create may already have committed) is surfaced unchanged. The helper is shared with the other
 * contact writers (two-way SMS plan §4.3) in utils/smsNumberBusy.
 */
export { isSmsNumberBusy, retryOnSmsNumberBusy };

/**
 * Create new contact
 */
export const createContact = async (
  contact: Partial<Contact>,
): Promise<Contact> => {
  const { data, error } = await retryOnSmsNumberBusy(() =>
    supabase.from("contacts").insert([contact]).select().single(),
  );

  if (error) {
    throw error;
  }
  return data;
};

/**
 * Update contact
 */
export const updateContact = async (
  id: UUID,
  updates: Partial<Contact>,
): Promise<Contact> => {
  const { data, error } = await retryOnSmsNumberBusy(() =>
    supabase.from("contacts").update(updates).eq("id", id).select().single(),
  );

  if (error) {
    throw error;
  }
  return data;
};

/**
 * Delete contact
 */
export const deleteContact = async (id: UUID): Promise<void> => {
  const { error } = await retryOnSmsNumberBusy(() =>
    supabase.from("contacts").delete().eq("id", id),
  );

  if (error) {
    throw error;
  }
};

/**
 * Get contact membership history
 */
export const getContactMembershipHistory = async (
  contactId: UUID,
): Promise<any[]> => {
  const { data, error } = await supabase
    .from("membership_history")
    .select("*")
    .eq("contact_id", contactId)
    .order("start_date", { ascending: false });

  if (error) {
    throw error;
  }
  return data || [];
};

/**
 * Add tags to contact via contact_tags table (trigger syncs contacts.tags array)
 */
export const addContactTags = async (
  contactId: UUID,
  tags: string[],
): Promise<void> => {
  for (const tag of tags) {
    await addContactTag({ contact_id: contactId, tag, source_type: "manual" });
  }
};

/**
 * Remove tags from contact via contact_tags table (trigger syncs contacts.tags array)
 */
export const removeContactTags = async (
  contactId: UUID,
  tags: string[],
): Promise<void> => {
  for (const tag of tags) {
    await removeContactTag(contactId, tag);
  }
};

export const getContactSubscriptions = async (
  contactId: UUID,
): Promise<ContactSubscription[]> => {
  const { data, error } = await supabase
    .from("contact_subscriptions")
    .select("*")
    .eq("contact_id", contactId)
    .order("term_start_date", { ascending: false });

  if (error) {
    throw error;
  }
  return (data || []) as ContactSubscription[];
};

export const getHouseholdMembers = async (
  householdKey: string,
  excludeContactId: UUID,
): Promise<HouseholdMember[]> => {
  const { data, error } = await supabase
    .from("contacts")
    .select("id, first_name, last_name, is_primary_member, member_status")
    .eq("household_key", householdKey)
    .neq("id", excludeContactId)
    .order("is_primary_member", { ascending: false })
    .order("last_name");

  if (error) {
    throw error;
  }
  return (data || []) as HouseholdMember[];
};

/**
 * ---------------------------------------------------------------------------
 * Contact Vouchers tab — canonical resolution
 * ---------------------------------------------------------------------------
 * Columns verified against the LIVE schema in BOTH environments (QA
 * qqynnldqhtstlfiimnwm, PROD nnzheppmjkoygpdbrqkj):
 *   vouchers  -> discount_value, applied_amount, current_value, status,
 *                is_redeemed, is_cancelled, reward_state, end_date, token_id,
 *                issued_at, redeemed_at, created_at, event_id, contact_id, email
 *   events    -> title            (there is NO events.name)
 *   redemption_tokens -> event_id
 * `vouchers.voucher_amount` and `events.name` do NOT exist in either
 * environment; selecting them made PostgREST reject the whole request (400).
 *
 * Effective event: COALESCE(vouchers.event_id, redemption_tokens.event_id via
 * token_id) — the SAME definition the Redemptions RPCs project as
 * `effective_event_id` (database/migrations/2026-08-15-038-redemptions-effective-event-sargable.sql).
 * It matters: on PROD event 51981c06-6bb4-4938-950f-fe8d6c6591c4, 77 of the 80
 * vouchers reach the event ONLY through token_id, so reading vouchers.event_id
 * alone left the Event column blank for nearly all of them. The event is never
 * inferred from email or timestamps.
 *
 * Effective status: computed by voucherStatusLabel() — the single frontend
 * authority, shared with the Redemptions page and its CSV export. The raw
 * `status` column is NOT trustworthy on its own: PROD voucher 6DXJOA0Q is
 * is_redeemed=true with status='active', and 2,590 PROD vouchers (761 in QA)
 * are redeemed while carrying a non-'redeemed' status.
 */
export const CONTACT_VOUCHERS_LIMIT = 200;

const CONTACT_VOUCHER_SELECT =
  "id, voucher_code, voucher_name, discount_value, applied_amount, current_value, " +
  "status, is_redeemed, is_cancelled, reward_state, end_date, token_id, " +
  "issued_at, redeemed_at, created_at, event_id, events(title), " +
  "redemption_tokens(event_id, events(title))";

export interface ContactVoucherRow {
  id: UUID;
  voucher_code: string;
  voucher_name: string | null;
  discount_value: number | null;
  applied_amount: number | null;
  current_value: number | null;
  status: string | null;
  is_redeemed: boolean | null;
  is_cancelled: boolean | null;
  reward_state: string | null;
  end_date: number | null;
  token_id: UUID | null;
  issued_at: string | null;
  redeemed_at: string | null;
  created_at: string;
  event_id: UUID | null;
  /** COALESCE(event_id, redemption_tokens.event_id) — matches the RPCs' effective_event_id. */
  effective_event_id: UUID | null;
  effective_event_title: string | null;
  /** voucherStatusLabel(): Test > Cancelled > Redeemed > Void/Pending > Expired > Active. */
  effective_status: string;
}

export interface ContactVouchersResult {
  rows: ContactVoucherRow[];
  /** True when the contact has MORE vouchers than `limit`; the caller must say so. */
  truncated: boolean;
  limit: number;
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * PostgREST `or=` takes a RAW, comma/paren-delimited string that supabase-js does not
 * escape. Only values free of those delimiters may be interpolated into it; anything
 * else is sent as its own request with a properly-encoded `.ilike()` instead.
 */
const isInlineSafeFilterValue = (v: string): boolean =>
  /^[^,()"'\\\s]+$/.test(v);

const firstEmbed = (v: unknown): any =>
  Array.isArray(v) ? (v[0] ?? null) : (v ?? null);

export const mapContactVoucherRow = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  v: any,
): ContactVoucherRow => {
  const directEvent = firstEmbed(v.events);
  const token = firstEmbed(v.redemption_tokens);
  const tokenEvent = token ? firstEmbed(token.events) : null;
  // COALESCE order is load-bearing: a direct event_id always wins, exactly as the RPCs do.
  const effectiveEventId = v.event_id ?? token?.event_id ?? null;
  const effectiveEventTitle = v.event_id
    ? (directEvent?.title ?? null)
    : (tokenEvent?.title ?? null);
  return {
    id: v.id,
    voucher_code: v.voucher_code,
    voucher_name: v.voucher_name ?? null,
    discount_value: v.discount_value ?? null,
    applied_amount: v.applied_amount ?? null,
    current_value: v.current_value ?? null,
    status: v.status ?? null,
    is_redeemed: v.is_redeemed ?? null,
    is_cancelled: v.is_cancelled ?? null,
    reward_state: v.reward_state ?? null,
    end_date: v.end_date ?? null,
    token_id: v.token_id ?? null,
    issued_at: v.issued_at ?? null,
    redeemed_at: v.redeemed_at ?? null,
    created_at: v.created_at,
    event_id: v.event_id ?? null,
    effective_event_id: effectiveEventId,
    effective_event_title: effectiveEventTitle,
    effective_status: voucherStatusLabel({
      status: v.status,
      is_cancelled: v.is_cancelled,
      is_redeemed: v.is_redeemed,
      token_id: v.token_id,
      reward_state: v.reward_state,
      end_date: v.end_date,
    }),
  };
};

/** issued_at DESC with NULLs last, then created_at DESC — mirrors the server ordering. */
const byIssuedThenCreatedDesc = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  a: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  b: any,
): number => {
  const ai = a.issued_at ? Date.parse(a.issued_at) : null;
  const bi = b.issued_at ? Date.parse(b.issued_at) : null;
  if (ai !== bi) {
    if (ai === null) {
      return 1;
    }
    if (bi === null) {
      return -1;
    }
    return bi - ai;
  }
  return Date.parse(b.created_at ?? 0) - Date.parse(a.created_at ?? 0);
};

/**
 * Vouchers belonging to a contact.
 *
 * Identity: `contact_id` is preferred, so a voucher stays attached across an email
 * change or a contact merge, and a voucher explicitly linked to a DIFFERENT contact is
 * never pulled in just because the addresses match. Email is a FALLBACK for vouchers
 * that carry no contact_id at all — 1,375 of 13,697 PROD rows (1,001 of 2,173 in QA),
 * so dropping it would hide real history.
 */
export const getContactVouchers = async (
  contact: { id?: UUID | null; email?: string | null } | null | undefined,
  options?: { limit?: number },
): Promise<ContactVouchersResult> => {
  const limit = Math.max(1, options?.limit ?? CONTACT_VOUCHERS_LIMIT);
  const contactId = (contact?.id ?? "").trim() || null;
  const email = (contact?.email ?? "").trim() || null;

  if (!contactId && !email) {
    return { rows: [], truncated: false, limit };
  }

  // limit + 1 so truncation is DETECTED rather than silently swallowed, which is what the
  // old un-surfaced .limit(100) did.
  const base = () =>
    supabase
      .from("vouchers")
      .select(CONTACT_VOUCHER_SELECT)
      .order("issued_at", { ascending: false, nullsFirst: false })
      .order("created_at", { ascending: false })
      .limit(limit + 1);

  const requests: PromiseLike<{ data: unknown; error: unknown }>[] = [];

  if (!contactId) {
    requests.push(base().ilike("email", email as string));
  } else if (
    email &&
    UUID_RE.test(contactId) &&
    isInlineSafeFilterValue(email)
  ) {
    // contact_id match, OR an orphan voucher (no contact_id) with this address.
    requests.push(
      base().or(
        `contact_id.eq.${contactId},and(contact_id.is.null,email.ilike."${email}")`,
      ),
    );
  } else {
    requests.push(base().eq("contact_id", contactId));
    if (email) {
      requests.push(base().is("contact_id", null).ilike("email", email));
    }
  }

  const results = await Promise.all(requests);
  for (const r of results) {
    if (r.error) {
      throw r.error;
    }
  }

  const seen = new Set<string>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const merged: any[] = [];
  for (const r of results) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    for (const row of (r.data as any[]) || []) {
      if (seen.has(row.id)) {
        continue;
      }
      seen.add(row.id);
      merged.push(row);
    }
  }
  merged.sort(byIssuedThenCreatedDesc);

  return {
    rows: merged.slice(0, limit).map(mapContactVoucherRow),
    truncated: merged.length > limit,
    limit,
  };
};

/**
 * Get contact relationships
 */
export const getContactRelationships = async (
  contactId: UUID,
): Promise<ContactRelationship[]> => {
  const { data, error } = await supabase
    .from("contact_relationships")
    .select("*")
    .or(`contact_id.eq.${contactId},related_contact_id.eq.${contactId}`)
    .order("created_at", { ascending: false });

  if (error) {
    throw error;
  }
  return data || [];
};

/**
 * Get contact by email address
 * Returns null if no contact found
 */
/**
 * Look up a contact by membership_id (the Zuora MemberNumber__c /
 * SecondaryMemberNumber__c value). Returns null if no match.
 *
 * Used by the support-task detail view to render customer_snapshot.member_id
 * as a clickable link to the matching contact record.
 */
export const getContactByMembershipId = async (
  membershipId: string,
): Promise<Contact | null> => {
  if (!membershipId || !membershipId.trim()) {
    return null;
  }
  const { data, error } = await supabase
    .from("contacts")
    .select("*")
    .eq("membership_id", membershipId.trim())
    .maybeSingle();

  if (error) {
    console.error("Error fetching contact by membership_id:", error);
    return null;
  }
  return data;
};

export const getContactByEmail = async (
  email: string,
): Promise<Contact | null> => {
  const { data, error } = await supabase
    .from("contacts")
    .select("*")
    .ilike("email", email.trim())
    .maybeSingle();

  if (error) {
    console.error("Error fetching contact by email:", error);
    return null;
  }
  return data;
};

/** Row returned by the shared contact picker search (search_contacts_picker). */
export type ContactPickerResult = Pick<
  Contact,
  | "id"
  | "first_name"
  | "last_name"
  | "email"
  | "phone_number"
  | "membership_id"
  | "member_status"
>;

/**
 * Shared contact picker search (Attorney Questions, Support Tasks quick
 * lookup, Create Task, Template Preview).
 *
 * Calls the SECURITY DEFINER RPC search_contacts_picker (migration
 * 2026-10-15-720). A direct PostgREST ILIKE on contacts cannot use the
 * trigram indexes under the contacts RLS policy (ILIKE is not leakproof),
 * so every name / phone / membership search seq-scanned the table and hit
 * the statement timeout. The RPC is limited to active internal staff
 * (42501 otherwise), excludes merged contacts and returns picker columns
 * only. Matching rules (First Last either order, e-mail, phone in any
 * format, membership number) live in the function.
 */
export const searchContacts = async (
  query: string,
  limit: number = 10,
): Promise<ContactPickerResult[]> => {
  const term = (query ?? "").trim();
  if (!contactPickerCanSearch(term)) {
    return [];
  }

  const { data, error } = await supabase.rpc("search_contacts_picker", {
    p_query: term,
    p_limit: limit,
  });

  if (error) {
    console.error("Error searching contacts:", error);
    throw error;
  }

  return (data ?? []) as ContactPickerResult[];
};

/**
 * Get recent vouchers for a contact (last N days)
 * Used to warn customer service about duplicate issuance
 */
export const getRecentVouchersForContact = async (
  contactId: UUID,
  days: number = 30,
): Promise<any[]> => {
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - days);

  const { data, error } = await supabase
    .from("vouchers")
    .select("*")
    .eq("contact_id", contactId)
    .gte("created_at", cutoffDate.toISOString())
    .order("created_at", { ascending: false });

  if (error) {
    console.error("Error fetching recent vouchers:", error);
    return [];
  }
  return data || [];
};

// ========================================
// CLASS COMPLETIONS (EDUCATION)
// ========================================

/**
 * Get class completions for a contact
 * Returns training completion records ordered by completion_date DESC
 */
export const getContactClassCompletions = async (
  contactId: UUID,
): Promise<ClassCompletion[]> => {
  const { data, error } = await supabase
    .from("class_completions")
    .select(
      "id, contact_id, class_id, class_name, class_start_date, completion_date, created_at",
    )
    .eq("contact_id", contactId)
    .order("completion_date", { ascending: false });

  if (error) {
    console.error("Error fetching class completions:", error);
    return [];
  }
  return data || [];
};

// ========================================
// EMAIL & MESSAGE HISTORY
// ========================================

export interface EmailTrackingRecord {
  id: string;
  flight_id: string | null;
  contact_id: string | null;
  email_address: string;
  sendgrid_message_id: string | null;
  sent_at: string | null;
  delivered_at: string | null;
  opened_at: string | null;
  clicked_at: string | null;
  bounced_at: string | null;
  bounce_reason: string | null;
  bounce_type: "hard" | "soft" | "spam" | null;
  unsubscribed_at: string | null;
  clicked_links: Array<{ url: string; clicked_at: string }>;
  metadata: Record<string, unknown>;
  voucher_id: string | null;
  created_at: string;
  // Joined data
  campaign_flight?: {
    id: string;
    name: string;
    campaign_id: string;
    campaign?: {
      id: string;
      name: string;
    };
  };
}

/**
 * Get email history for a contact
 * Returns email tracking records with campaign/flight info
 */
export const getContactEmailHistory = async (
  contactId: UUID,
  limit: number = 50,
): Promise<EmailTrackingRecord[]> => {
  const { data, error } = await supabase
    .from("email_tracking")
    .select(
      `
      *,
      campaign_flight:campaign_flights!flight_id (
        id,
        name,
        campaign_id,
        campaign:campaigns!campaign_id (
          id,
          name
        )
      )
    `,
    )
    .eq("contact_id", contactId)
    .order("sent_at", { ascending: false, nullsFirst: false })
    .order("created_at", { ascending: false })
    .limit(limit);

  if (error) {
    console.error("Error fetching email history:", error);
    return [];
  }

  return data || [];
};

/**
 * A staff quick-email (Contact page "Send Email") from the canonical transactional_email_log,
 * exposed via the role-gated get_contact_quick_email_history RPC. Distinct from campaign
 * email_tracking — these carry no open/click webhooks, so we report acceptance, not delivery.
 */
export interface QuickEmailRecord {
  id: string;
  subject: string | null;
  outcome: "sent" | "suppressed_skip" | "send_failed" | string;
  provider: string | null;
  provider_message_id: string | null;
  provider_error: string | null;
  caller_user_id: string | null;
  sender_name: string | null;
  // Present when this quick email was sent using a SendGrid dynamic template (best-effort
  // correlation in get_contact_quick_email_history). null for a plain-text quick email.
  template_id: string | null;
  template_name: string | null;
  created_at: string;
}

/**
 * Staff quick emails sent to this contact. Read-gated in the DB to internal sender roles; a
 * non-permitted caller gets an authorization error, which we swallow to an empty list (the tab
 * simply won't show a quick-email section).
 */
export const getContactQuickEmailHistory = async (
  contactId: UUID,
): Promise<QuickEmailRecord[]> => {
  const { data, error } = await supabase.rpc(
    "get_contact_quick_email_history",
    {
      p_contact_id: contactId,
    },
  );

  if (error) {
    // A role not permitted to view quick-email history gets a definer 'not authorized' RAISE — an
    // EXPECTED empty state, so return []. Any OTHER error (DB/migration/timeout) MUST surface so the UI
    // can show "unavailable" rather than silently masking a failure as "no history" (audit LOW).
    if (/not[_ ]authorized/i.test(error.message || "")) {
      return [];
    }
    throw error;
  }

  return (data as QuickEmailRecord[]) || [];
};

export interface SmsTrackingRecord {
  id: string;
  flight_id: string | null;
  contact_id: string | null;
  phone_number: string;
  provider_message_id: string | null;
  message_body: string;
  sent_at: string | null;
  delivered_at: string | null;
  failed_at: string | null;
  failure_reason: string | null;
  segments_used: number | null;
  metadata: Record<string, unknown>;
  created_at: string;
  /** "mms" when the message carried an image (migration 2026-09-28-008). */
  transport?: "sms" | "mms";
  media?: {
    media_id: string;
    content_type: string;
    storage_path: string;
    sha256: string;
  }[];
  /** The provider MAY have queued it; not failed, never resent automatically. */
  uncertain_dispatch?: { reason: string; at: string } | null;
  // Joined data
  campaign_flight?: {
    id: string;
    name: string;
    campaign_id: string;
    campaign?: {
      id: string;
      name: string;
    };
  };
}

/**
 * Get SMS history for a contact
 * Returns SMS tracking records with campaign/flight info
 */
export const getContactSmsHistory = async (
  contactId: UUID,
  limit: number = 50,
): Promise<SmsTrackingRecord[]> => {
  // ⚠️ RPC, not a table select. Migration 2026-09-24-020 closed the authenticated grant on
  // sms_tracking (it had been world-writable), which silently broke the direct query this used to
  // make -- it returned nothing for every caller. Nothing looked broken only because the table is
  // empty. Access is now decided by role inside sms_contact_history.
  const { data, error } = await supabase.rpc("sms_contact_history", {
    p_contact_id: contactId,
    p_limit: limit,
  });

  if (error) {
    console.error("Error fetching SMS history:", error);
    throw new Error(`Failed to fetch SMS history: ${error.message}`);
  }

  return (data ?? []) as unknown as SmsTrackingRecord[];
};

export interface SequenceMessageRecord {
  id: string;
  contact_id: string;
  channel: string;
  email_address: string | null;
  email_subject: string | null;
  scheduled_for: string | null;
  status: string;
  sent_at: string | null;
  delivered_at: string | null;
  failed_at: string | null;
  failure_reason: string | null;
  created_at: string;
  enrollment?: {
    sequence_id: string;
    sequence?: {
      id: string;
      name: string;
    };
  };
}

export const getContactSequenceHistory = async (
  contactId: UUID,
  limit: number = 50,
): Promise<SequenceMessageRecord[]> => {
  const { data, error } = await supabase
    .from("scheduled_messages")
    .select(
      `
      id, contact_id, channel, email_address, email_subject,
      scheduled_for, status, sent_at, delivered_at, failed_at,
      failure_reason, created_at,
      enrollment:sequence_enrollments!enrollment_id (
        sequence_id,
        sequence:message_sequences!sequence_id (
          id,
          name
        )
      )
    `,
    )
    .eq("contact_id", contactId)
    .eq("source_type", "sequence")
    .eq("channel", "email")
    .order("sent_at", { ascending: false, nullsFirst: false })
    .order("created_at", { ascending: false })
    .limit(limit);

  if (error) {
    console.error("Error fetching sequence message history:", error);
    return [];
  }

  return (data || []) as SequenceMessageRecord[];
};

/**
 * An ACTIVE sequence enrollment for a contact — the automated sequences the contact is currently
 * enrolled in (distinct from the individual sequence MESSAGES already sent, above). Read from
 * sequence_enrollments joined to message_sequences for the human-readable sequence name.
 */
export interface SequenceEnrollmentRecord {
  id: string;
  sequence_id: string;
  status: string;
  enrolled_at: string;
  next_action_at: string | null;
  sequence?: {
    id: string;
    name: string;
  } | null;
}

/**
 * Active sequence enrollments for a contact (status='active'). Powers the "Enrolled in sequences"
 * section of the Email History tab. Bounded select (never an unbounded scan). RLS on
 * sequence_enrollments / message_sequences restricts reads to superadmin/admin/manager/staff — other
 * roles simply get an empty list (no error), so the section degrades gracefully rather than blanking
 * the tab. Any real error is swallowed to [] (the rest of the tab still renders).
 */
export const getContactSequenceEnrollments = async (
  contactId: UUID,
  limit: number = 50,
): Promise<SequenceEnrollmentRecord[]> => {
  const { data, error } = await supabase
    .from("sequence_enrollments")
    .select(
      `
      id, sequence_id, status, enrolled_at, next_action_at,
      sequence:message_sequences!sequence_id (
        id,
        name
      )
    `,
    )
    .eq("contact_id", contactId)
    .eq("status", "active")
    .order("enrolled_at", { ascending: false })
    .limit(limit);

  if (error) {
    console.error("Error fetching sequence enrollments:", error);
    return [];
  }

  return (data || []) as unknown as SequenceEnrollmentRecord[];
};

export interface EventCheckinRecord {
  id: string;
  event_id: string;
  contact_id: string | null;
  checked_in_at: string;
  check_in_location: string | null;
  registration_source: string | null;
  registration_date: string | null;
  attendance_status: string | null;
  event?: {
    title: string | null;
    start_date: string | null;
    start_time: string | null;
    location_name: string | null;
    type: string | null;
  } | null;
}

/**
 * Get the events this contact has CHECKED IN to (attended), newest first.
 * Driven by event_attendees.checked_in_at, which every check-in path sets
 * (QR/code scan, staff walk-in, public self check-in). Read-only.
 */
export const getContactEventCheckins = async (
  contactId: UUID,
  limit: number = 100,
): Promise<EventCheckinRecord[]> => {
  const { data, error } = await supabase
    .from("event_attendees")
    .select(
      `
      id, event_id, contact_id, checked_in_at, check_in_location,
      registration_source, registration_date, attendance_status,
      event:events!event_id ( title, start_date, start_time, location_name, type )
    `,
    )
    .eq("contact_id", contactId)
    .not("checked_in_at", "is", null)
    .order("checked_in_at", { ascending: false })
    .limit(limit);

  if (error) {
    console.error("Error fetching event check-ins:", error);
    return [];
  }

  return (data || []) as unknown as EventCheckinRecord[];
};
